// @file Composable for undoable actions
import {
  ActionHistoryType,
  recordRedoActionHistory,
  redo,
  undo,
  type ActionHistoryRecord,
} from '@@/bits/action_history_manager'
import device from '@@/bits/device'
import { n__, __ } from '@@/bits/intl'
import { SnackbarNotificationType } from '@@/enums'
import { useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceDraftsStore } from '@@/pinia/surface_drafts'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import type { DraftPost, Post } from '@@/types'
import { useKeyboardShortcutsForUndoRedo } from '@@/vuecomposables/keyboard_shortcuts'
import { ref } from 'vue'

export const DELETE_SNACKBAR_TIMEOUT = 5000
const UNDO_DELETE_ACTION_TEXT_DESKTOP_WINDOWS = __('Undo (Ctrl+Z)')
const UNDO_DELETE_ACTION_TEXT_DESKTOP_MAC = __('Undo (Cmd+Z)')
const UNDO_DELETE_ACTION_TEXT_MOBILE = __('Undo')

const getUndoDeleteActionText = (): string => {
  if (!device.desktop) {
    return UNDO_DELETE_ACTION_TEXT_MOBILE
  }
  return device.windows ? UNDO_DELETE_ACTION_TEXT_DESKTOP_WINDOWS : UNDO_DELETE_ACTION_TEXT_DESKTOP_MAC
}
const activeSnackbarUids = ref<Record<string, string>>({})

export const useUndoable = (): {
  registerKeyboardShortcuts: () => void
  extractDraftDataForUndoRedo: (draft: DraftPost) => DraftPost
  extractPostDataForUndoRedo: (post: Post) => Post | null
  setDeletedSnackbar: (
    actionType: ActionHistoryType,
    actionHistoryId: string,
    data: ActionHistoryRecord['data'],
  ) => void
  setRestoredSnackbar: (
    actionType: ActionHistoryType,
    actionHistoryId: string,
    data: ActionHistoryRecord['data'],
  ) => void
} => {
  const surfaceStore = useSurfaceStore()
  const surfaceDraftsStore = useSurfaceDraftsStore()
  const surfacePostsStore = useSurfacePostsStore()
  const globalSnackbarStore = useGlobalSnackbarStore()

  function handleUndo(event: KeyboardEvent): void {
    // Don't trigger undo if the user has an active draft
    if (surfaceDraftsStore.isAnyDraftActive) return

    const action = undo()
    if (action == null) return

    // If the user is on a browser default undoable action (e.g. text input, reopen closed tab), they would trigger
    // both that undoable action and the undoable action we track in our stack
    event.preventDefault()
    event.stopPropagation()

    switch (action.type) {
      case ActionHistoryType.DELETE_DRAFTS:
        surfaceDraftsStore.undoDeleteDrafts(action.data)
        setRestoredSnackbar(ActionHistoryType.DELETE_DRAFTS, action.id, action.data)
        break
      case ActionHistoryType.DELETE_POSTS:
        event.preventDefault()
        surfacePostsStore.undoDeletePosts(action.data)
        setRestoredSnackbar(ActionHistoryType.DELETE_POSTS, action.id, action.data)
        break
    }
  }

  function handleRedo(event: KeyboardEvent): void {
    // We don't check for active draft here unlike handleUndo because the user might want to redo their restored draft that is active
    const action = redo()
    if (action == null) return

    // If the user is on a browser default undoable action (e.g. text input, reopen closed tab), they would trigger
    // both that undoable action and the undoable action we track in our stack
    event.preventDefault()
    event.stopPropagation()

    switch (action.type) {
      case ActionHistoryType.DELETE_DRAFTS:
        void surfaceDraftsStore.redoDeleteDrafts(action.data, { actionHistoryId: action.id })
        setDeletedSnackbar(ActionHistoryType.DELETE_DRAFTS, action.id, action.data)
        break
      case ActionHistoryType.DELETE_POSTS:
        surfacePostsStore.redoDeletePosts(action.data)
        recordRedoActionHistory(ActionHistoryType.DELETE_POSTS, action.data, action.id)
        setDeletedSnackbar(ActionHistoryType.DELETE_POSTS, action.id, action.data)
        break
    }
  }

  function registerKeyboardShortcuts(): void {
    useKeyboardShortcutsForUndoRedo({
      handleUndo,
      handleRedo,
    })
  }

  function extractDraftDataForUndoRedo(draft: DraftPost): DraftPost {
    // the undo action will recreate id and draftCreatedAt, so we remove it to avoid duplicates
    // remove attachment related fields because they cannot be supported
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const { id, draftCreatedAt, attachment, attachment_caption, attachment_link, wish_content, ...draftData } = draft
    return draftData
  }

  function extractPostDataForUndoRedo(post: Post): Post | null {
    // we should not support undo for posts created by other users currently because the recreated post will
    // have the current user as the author, and this will break the post author's permission check
    if (post.author_id !== surfaceStore.user.id) {
      return null
    }
    // the undo action will recreate id and published_at, so we remove it to avoid duplicates
    // remove attachment related fields because they cannot be supported
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const { id, published_at, attachment, attachment_caption, attachment_link, wish_content, ...postData } = post
    return postData
  }

  const getDeletedSnackbarMessage = (actionType: ActionHistoryType, count: number): string => {
    switch (actionType) {
      case ActionHistoryType.DELETE_DRAFTS:
        return n__('Draft deleted', '%{count} drafts deleted', count, { count })
      case ActionHistoryType.DELETE_POSTS:
        return n__('Post deleted', '%{count} posts deleted', count, { count })
      default:
        return ''
    }
  }

  const getRestoredSnackbarMessage = (actionType: ActionHistoryType, count: number): string => {
    switch (actionType) {
      case ActionHistoryType.DELETE_DRAFTS:
        return n__('Draft restored', '%{count} drafts restored', count, { count })
      case ActionHistoryType.DELETE_POSTS:
        return n__('Post restored', '%{count} posts restored', count, { count })
      default:
        return ''
    }
  }

  function handleUndoDeleteSnackbarAction(actionType: ActionHistoryType, data: ActionHistoryRecord['data']): void {
    switch (actionType) {
      case ActionHistoryType.DELETE_DRAFTS:
        surfaceDraftsStore.handleUndoDeleteSpecificDrafts(data)
        break
      case ActionHistoryType.DELETE_POSTS:
        surfacePostsStore.handleUndoDeleteSpecificPosts(data)
        break
      default:
    }
  }

  function setDeletedSnackbar(
    actionType: ActionHistoryType,
    actionHistoryId: string,
    data: ActionHistoryRecord['data'],
  ): void {
    let snackbarTriggerUid: string
    if (
      activeSnackbarUids.value[actionHistoryId] != null &&
      globalSnackbarStore.isSnackbarBeingShown(activeSnackbarUids.value[actionHistoryId])
    ) {
      snackbarTriggerUid = activeSnackbarUids.value[actionHistoryId]
      globalSnackbarStore.updateSnackbar(snackbarTriggerUid, {
        notificationType: SnackbarNotificationType.success,
        message: getDeletedSnackbarMessage(actionType, data.length),
        actionText: getUndoDeleteActionText(),
        actionTextActions: [() => handleUndoDeleteSnackbarAction(actionType, data)],
        timeout: DELETE_SNACKBAR_TIMEOUT,
      })
    } else {
      snackbarTriggerUid = globalSnackbarStore.setSnackbar({
        notificationType: SnackbarNotificationType.success,
        message: getDeletedSnackbarMessage(actionType, data.length),
        actionText: getUndoDeleteActionText(),
        actionTextActions: [() => handleUndoDeleteSnackbarAction(actionType, data)],
        timeout: DELETE_SNACKBAR_TIMEOUT,
      })
    }
    activeSnackbarUids.value[actionHistoryId] = snackbarTriggerUid
  }

  function setRestoredSnackbar(
    actionType: ActionHistoryType,
    actionHistoryId: string,
    data: ActionHistoryRecord['data'],
  ): void {
    let snackbarTriggerUid: string
    if (
      activeSnackbarUids.value[actionHistoryId] != null &&
      globalSnackbarStore.isSnackbarBeingShown(activeSnackbarUids.value[actionHistoryId])
    ) {
      snackbarTriggerUid = activeSnackbarUids.value[actionHistoryId]
      globalSnackbarStore.updateSnackbar(snackbarTriggerUid, {
        notificationType: SnackbarNotificationType.success,
        message: getRestoredSnackbarMessage(actionType, data.length),
        actionText: undefined,
        actionTextActions: [],
        timeout: DELETE_SNACKBAR_TIMEOUT,
      })
    } else {
      snackbarTriggerUid = globalSnackbarStore.setSnackbar({
        notificationType: SnackbarNotificationType.success,
        message: getRestoredSnackbarMessage(actionType, data.length),
        actionText: undefined,
        actionTextActions: [],
        timeout: DELETE_SNACKBAR_TIMEOUT,
      })
    }
    activeSnackbarUids.value[actionHistoryId] = snackbarTriggerUid
  }

  return {
    registerKeyboardShortcuts,
    extractDraftDataForUndoRedo,
    extractPostDataForUndoRedo,
    setDeletedSnackbar,
    setRestoredSnackbar,
  }
}
